import type { ChapterCharacterLayer } from '../types/characterLayerTypes'

export const CHARACTER_DETECTOR_RUN_HISTORY_METADATA_KEY = 'character_detector_runs_v1'
export const CHARACTER_DETECTOR_RUN_HISTORY_LIMIT = 8

let characterDetectorRunIdSequence = 0

export const createCharacterDetectorRunId = (prefix: string): string => {
  characterDetectorRunIdSequence += 1
  return `${prefix}_${Date.now().toString(36)}_${characterDetectorRunIdSequence.toString(36)}`
}

interface CharacterDetectorModelCallForPersistence {
  promptText?: string
  sourceText?: string
  promptVersion?: string
  sourceTextLength?: number
  windowId?: string
  windowIndex?: number
  windowCount?: number
  rowLabelRange?: { start: string; end: string } | null
  sourceRowIds?: readonly string[]
}

export interface CharacterDetectorRunForPersistence {
  runId: string
  runMode: string
  status: string
  startedAt: string
  completedAt?: string
  resumedFromRunId?: string
  retriedFromRunId?: string
  provenance: {
    detectorId?: string
    detectorLabel?: string
    extractionUnit?: string
    sourceLabel?: string
    witnessLabels?: readonly string[]
    modelId?: string
    scorer?: string
    temperature?: number
    thinkingLevel?: string
    promptVersion?: string
    promptPreview?: string
    modelCallContexts?: CharacterDetectorModelCallForPersistence[]
    figureSource?: string
    figureCount?: number
    focusedCharacterId?: string
    focusedCharacterName?: string
    rowCount?: number
    windowCount?: number
    rowLabels?: readonly string[]
    [key: string]: unknown
  }
  resultSet?: Record<string, number>
}

export interface DurableCharacterDetectorModelCallFingerprint {
  promptSha256?: string
  sourceTextSha256?: string
  promptVersion?: string
  sourceTextLength?: number
  windowId?: string
  windowIndex?: number
  windowCount?: number
  rowLabelRange?: { start: string; end: string } | null
  sourceRowIds?: string[]
}

export interface DurableCharacterDetectorProvenance {
  detectorId?: string
  detectorLabel?: string
  extractionUnit?: string
  sourceLabel?: string
  witnessLabels?: string[]
  modelId?: string
  scorer?: string
  temperature?: number
  thinkingLevel?: string
  promptVersion?: string
  figureSource?: string
  figureCount?: number
  focusedCharacterId?: string
  focusedCharacterName?: string
  rowCount?: number
  windowCount?: number
  rowLabels?: string[]
}

export interface DurableCharacterDetectorRunRecord {
  schema: 'variorum.character-detector-run.v1'
  runId: string
  runMode: string
  status: string
  startedAt: string
  completedAt?: string
  resumedFromRunId?: string
  retriedFromRunId?: string
  provenance: DurableCharacterDetectorProvenance
  modelCallFingerprints: DurableCharacterDetectorModelCallFingerprint[]
  resultSet?: Record<string, number>
  acceptedLayerSha256?: string
}

async function sha256(value: string): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new Error('SHA-256 is unavailable; Character detector provenance was not persisted.')
  }
  const bytes = new TextEncoder().encode(value)
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest))
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('')
}

export async function buildDurableCharacterDetectorRunRecord(
  run: CharacterDetectorRunForPersistence
): Promise<DurableCharacterDetectorRunRecord> {
  const source = run.provenance || {}
  const modelCallContexts = Array.isArray(source.modelCallContexts) ? source.modelCallContexts : []
  const copyString = (value: unknown): string | undefined => (
    typeof value === 'string' && value.length > 0 ? value : undefined
  )
  const copyNumber = (value: unknown): number | undefined => (
    typeof value === 'number' && Number.isFinite(value) ? value : undefined
  )
  const copyStrings = (value: unknown): string[] | undefined => (
    Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : undefined
  )
  // This is deliberately an allowlist. Detector responses, diagnostics, prompts,
  // and nested provider payloads must never enter the durable Character layer.
  const safeProvenance: DurableCharacterDetectorProvenance = {
    ...(copyString(source.detectorId) ? { detectorId: copyString(source.detectorId) } : {}),
    ...(copyString(source.detectorLabel) ? { detectorLabel: copyString(source.detectorLabel) } : {}),
    ...(copyString(source.extractionUnit) ? { extractionUnit: copyString(source.extractionUnit) } : {}),
    ...(copyString(source.sourceLabel) ? { sourceLabel: copyString(source.sourceLabel) } : {}),
    ...(copyStrings(source.witnessLabels) ? { witnessLabels: copyStrings(source.witnessLabels) } : {}),
    ...(copyString(source.modelId) ? { modelId: copyString(source.modelId) } : {}),
    ...(copyString(source.scorer) ? { scorer: copyString(source.scorer) } : {}),
    ...(copyNumber(source.temperature) !== undefined ? { temperature: copyNumber(source.temperature) } : {}),
    ...(copyString(source.thinkingLevel) ? { thinkingLevel: copyString(source.thinkingLevel) } : {}),
    ...(copyString(source.promptVersion) ? { promptVersion: copyString(source.promptVersion) } : {}),
    ...(copyString(source.figureSource) ? { figureSource: copyString(source.figureSource) } : {}),
    ...(copyNumber(source.figureCount) !== undefined ? { figureCount: copyNumber(source.figureCount) } : {}),
    ...(copyString(source.focusedCharacterId) ? { focusedCharacterId: copyString(source.focusedCharacterId) } : {}),
    ...(copyString(source.focusedCharacterName) ? { focusedCharacterName: copyString(source.focusedCharacterName) } : {}),
    ...(copyNumber(source.rowCount) !== undefined ? { rowCount: copyNumber(source.rowCount) } : {}),
    ...(copyNumber(source.windowCount) !== undefined ? { windowCount: copyNumber(source.windowCount) } : {}),
    ...(copyStrings(source.rowLabels) ? { rowLabels: copyStrings(source.rowLabels) } : {}),
  }

  const modelCallFingerprints = await Promise.all(modelCallContexts.map(async call => ({
    ...(typeof call.promptText === 'string' && call.promptText.length > 0
      ? { promptSha256: await sha256(call.promptText) }
      : {}),
    ...(typeof call.sourceText === 'string' && call.sourceText.length > 0
      ? { sourceTextSha256: await sha256(call.sourceText) }
      : {}),
    ...(call.promptVersion ? { promptVersion: call.promptVersion } : {}),
    ...(Number.isFinite(call.sourceTextLength) ? { sourceTextLength: call.sourceTextLength } : {}),
    ...(call.windowId ? { windowId: call.windowId } : {}),
    ...(Number.isInteger(call.windowIndex) ? { windowIndex: call.windowIndex } : {}),
    ...(Number.isInteger(call.windowCount) ? { windowCount: call.windowCount } : {}),
    ...(call.rowLabelRange !== undefined ? { rowLabelRange: call.rowLabelRange } : {}),
    ...(Array.isArray(call.sourceRowIds) ? { sourceRowIds: [...call.sourceRowIds] } : {})
  })))

  return {
    schema: 'variorum.character-detector-run.v1',
    runId: run.runId,
    runMode: run.runMode,
    status: run.status,
    startedAt: run.startedAt,
    ...(run.completedAt ? { completedAt: run.completedAt } : {}),
    ...(run.resumedFromRunId ? { resumedFromRunId: run.resumedFromRunId } : {}),
    ...(run.retriedFromRunId ? { retriedFromRunId: run.retriedFromRunId } : {}),
    provenance: safeProvenance,
    modelCallFingerprints,
    ...(run.resultSet ? {
      resultSet: Object.fromEntries(Object.entries(run.resultSet).filter((entry): entry is [string, number] => (
        typeof entry[1] === 'number' && Number.isFinite(entry[1])
      )))
    } : {})
  }
}

export function readDurableCharacterDetectorRuns(
  layer: ChapterCharacterLayer | null | undefined
): DurableCharacterDetectorRunRecord[] {
  const records = layer?.metadata?.[CHARACTER_DETECTOR_RUN_HISTORY_METADATA_KEY]
  if (!Array.isArray(records)) {
    return []
  }
  return records.filter((record): record is DurableCharacterDetectorRunRecord => (
    record?.schema === 'variorum.character-detector-run.v1'
      && typeof record.runId === 'string'
      && typeof record.runMode === 'string'
      && typeof record.status === 'string'
      && typeof record.startedAt === 'string'
      && record.provenance
      && typeof record.provenance === 'object'
      && Array.isArray(record.modelCallFingerprints)
  ))
}

export async function appendDurableCharacterDetectorRun(
  layer: ChapterCharacterLayer,
  run: CharacterDetectorRunForPersistence
): Promise<ChapterCharacterLayer> {
  const durableRun = await buildDurableCharacterDetectorRunRecord(run)
  const existing = readDurableCharacterDetectorRuns(layer)
  const merged = [
    durableRun,
    ...existing.filter(candidate => candidate.runId !== durableRun.runId)
  ]
  const referencedRunIds = new Set<string>([
    ...layer.characters.map(character => character.source_run_id),
    ...layer.occurrences.map(occurrence => occurrence.source_run_id),
    ...layer.notes.map(note => note.metadata?.source_run_id),
  ].filter((runId): runId is string => typeof runId === 'string' && runId.length > 0))
  const runs = merged.filter((candidate, index) => (
    index < CHARACTER_DETECTOR_RUN_HISTORY_LIMIT || referencedRunIds.has(candidate.runId)
  ))
  durableRun.acceptedLayerSha256 = await sha256(JSON.stringify({
    chapterId: layer.chapter_id,
    bookId: layer.book_id,
    version: layer.version,
    characters: layer.characters,
    occurrences: layer.occurrences,
    notes: layer.notes,
  }))

  return {
    ...layer,
    metadata: {
      ...(layer.metadata || {}),
      [CHARACTER_DETECTOR_RUN_HISTORY_METADATA_KEY]: runs
    }
  }
}
